import { useEffect, useCallback } from 'react';

interface UseInactivityTimerProps {
  timeout: number; // timeout in milliseconds
  onTimeout: () => void;
  enabled?: boolean;
  checkInterval?: number; // interval to check for inactivity in milliseconds
}

export const useInactivityTimer = ({
  timeout,
  onTimeout,
  enabled = true,
  checkInterval = 10000, // Default 10 seconds
}: UseInactivityTimerProps) => {
  const updateExpiryTime = useCallback(() => {
    if (!enabled) return;

    const expireTime = Date.now() + timeout;
    sessionStorage.setItem('expireTime', expireTime.toString());
  }, [timeout, enabled]);

  const checkForInactivity = useCallback(() => {
    if (!enabled) return;

    const expireTimeString = sessionStorage.getItem('expireTime');
    const expireTime = expireTimeString ? parseInt(expireTimeString, 10) : 0;
    const currentTime = Date.now();

    if (expireTime < currentTime) {
      onTimeout();
    }
  }, [enabled, onTimeout]);

  const handleActivity = useCallback(() => {
    updateExpiryTime();
  }, [updateExpiryTime]);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    // Set initial expiry time
    updateExpiryTime();

    // Check for inactivity periodically
    const interval = setInterval(() => {
      checkForInactivity();
    }, checkInterval);

    // List of events that indicate user activity
    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
    ];

    // Add event listeners
    events.forEach((event) => {
      document.addEventListener(event, handleActivity, true);
    });

    // Cleanup function
    return () => {
      clearInterval(interval);
      events.forEach((event) => {
        document.removeEventListener(event, handleActivity, true);
      });
    };
  }, [
    enabled,
    checkInterval,
    updateExpiryTime,
    checkForInactivity,
    handleActivity,
  ]);

  return {
    updateExpiryTime,
    checkForInactivity,
  };
};
